import { UserOutlined, LockOutlined, GlobalOutlined, LoginOutlined } from "@ant-design/icons";
import { Form, Input, Button, Select, Card, Typography, Space, Layout, theme } from "antd";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import ThemeToggle from "../../components/ThemeToggle";
import { ADMIN_DASHBOARD, backendUrlByEnv, envOptions } from "../../constants/routes";
import { useTheme } from "../../contexts/ThemeContext";
import * as actionTypes from "../../redux/actions";
import { showErrorNotification } from "../../utils/notification";

const { Title, Text } = Typography;
const { Option } = Select;
const { useToken } = theme;

const StyledLayout = styled(Layout)`
  box-sizing: border-box;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  transition: all 0.3s ease;
`;

const GlassCard = styled(Card)`
  width: 100%;
  max-width: 420px;
  border-radius: 16px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;

  .ant-card-body {
    padding: 40px 32px;
  }
`;

const FloatingToggle = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;
`;

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useToken();
  const { isDarkMode } = useTheme();

  const handleFormSubmit = (values) => {
    axios
      .post(`${backendUrlByEnv(values?.env)}/admin/login`, {
        ...values
      })
      .then((res) => {
        const { adminConfig = {} } = res.data;
        dispatch({
          type: actionTypes.SET_AUTH_INFO,
          authInfo: {
            isLoggedIn: true,
            config: adminConfig,
            env: values?.env
          }
        });
        navigate(ADMIN_DASHBOARD);
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  return (
    <StyledLayout
      style={{
        background: isDarkMode ? "#000000" : token.colorBgLayout,
        backgroundImage: isDarkMode
          ? "radial-gradient(circle at 50% 50%, #1a1a1a 0%, #000000 100%)"
          : "radial-gradient(circle at bottom right, #e6f7ff 0%, #ffffff 100%)"
      }}
    >
      <FloatingToggle>
        <ThemeToggle />
      </FloatingToggle>

      <GlassCard
        bordered={false}
        style={{
          background: token.colorBgContainer + "80" // Transparency
        }}
      >
        <Space direction="vertical" align="center" style={{ width: "100%", marginBottom: 32 }}>
          <Title level={3} style={{ margin: 0, color: token.colorPrimary, fontWeight: 700 }}>
            Welcome Back
          </Title>
          <Text type="secondary">Sign in to your admin account</Text>
        </Space>

        <Form
          form={form}
          name="login_input"
          onFinish={handleFormSubmit}
          initialValues={{
            env: localStorage.getItem("adminSelectedEnvironment") || envOptions[0]?.value
          }}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: "Please enter your email!" },
              { type: "email", message: "Invalid email format!" }
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Email Address" />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: "Please enter your password!" }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Password" />
          </Form.Item>

          <Form.Item name="env" rules={[{ required: true, message: "Select an environment!" }]}>
            <Select
              prefix={<GlobalOutlined />}
              placeholder="Environment"
              onChange={(value) => localStorage.setItem("adminSelectedEnvironment", value)}
            >
              {envOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" block icon={<LoginOutlined />} shape="round">
              Sign In
            </Button>
          </Form.Item>
        </Form>
      </GlassCard>
    </StyledLayout>
  );
};

export default Login;
